import { useId, useState } from 'react'
import { Box, Button, RadioGroup, Stack, Typography } from '@mui/material'
import { useNavigate, useParams } from 'react-router-dom'
import { screenPath } from '@commerce/core'
import { ActionBar, Screen } from '../design'
import { DISTRIBUTORS, useDemo } from '../demo'
import { DistributorCard } from '../components/DistributorCard'

/**
 * The design writes the count as a word — "Dos distribuidores atienden tu
 * tienda" — against a fixture of two. Spelling out an arbitrary number is not
 * worth a number-to-words table, so the words stop at four and numerals take
 * over. The singular and the empty case are here because a direct URL reaches
 * this screen whatever the list holds.
 */
const COUNT_WORDS: Record<number, string> = { 2: 'Dos', 3: 'Tres', 4: 'Cuatro' }

const headline = (count: number): string => {
  if (count === 0) return 'Ningún distribuidor atiende tu tienda todavía'
  if (count === 1) return 'Un distribuidor atiende tu tienda'
  return `${COUNT_WORDS[count] ?? count} distribuidores atienden tu tienda`
}

/**
 * Choosing which distributor to order from.
 *
 * Distributors have no endpoint in the ported domain — the list is
 * `demo/fixtures.ts` and the choice lands in `DemoContext`, not in Redux and not
 * on a server. What the choice actually changes today is the delivery days and
 * credit line the later screens quote; the catalog is the same either way.
 *
 * Selection is local until "Continuar", the way the design has it: the
 * highlighted card is a pending answer, and `chooseDistributor` is what commits
 * it. That is also why the initial selection falls back to the first
 * distributor rather than to nothing — the design opens preselected, and
 * `useDemo().distributor` already resolves an unset choice the same way.
 */
export const DistributorPage = () => {
  const { slug = '', sessionId = '' } = useParams()
  const navigate = useNavigate()
  const { distributorId, chooseDistributor } = useDemo()

  const [selectedId, setSelectedId] = useState(distributorId ?? DISTRIBUTORS[0]?.id ?? '')
  const headingId = useId()

  const confirmDistributor = () => {
    chooseDistributor(selectedId)
    navigate(screenPath('catalog', { slug, sessionId }))
  }

  return (
    <Screen testId="distributor-page">
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: 2.25,
          px: 3,
          py: 3,
        }}
      >
        <Stack spacing={1} sx={{ textAlign: 'center' }}>
          <Typography variant="h4" component="h1" id={headingId} sx={{ fontWeight: 700 }}>
            {headline(DISTRIBUTORS.length)}
          </Typography>
          {DISTRIBUTORS.length > 0 && (
            <Typography variant="body2" color="text.secondary">
              Elige con cuál quieres pedir hoy.
            </Typography>
          )}
        </Stack>

        <RadioGroup
          aria-labelledby={headingId}
          value={selectedId}
          onChange={(event) => setSelectedId(event.target.value)}
          sx={{ gap: 1.5 }}
        >
          {DISTRIBUTORS.map((distributor) => (
            <DistributorCard
              key={distributor.id}
              distributor={distributor}
              selected={distributor.id === selectedId}
            />
          ))}
        </RadioGroup>
      </Box>

      <ActionBar>
        <Button
          variant="contained"
          size="large"
          fullWidth
          disabled={!selectedId}
          onClick={confirmDistributor}
        >
          Continuar
        </Button>
      </ActionBar>
    </Screen>
  )
}
